import { Vector3, Face3 } from "three";
import { IHornDesignResponse } from "../../interfaces";

export interface IVerticesAndFaces {
  edgeFaces: Face3[];
  fanFaces: Face3[];
  fanVertices: Vector3[];
  edgeVertices: Vector3[];
}

export const euclideanDistance = (a: number, b: number) =>
  Math.sqrt(Math.pow(a, 2) + Math.pow(b, 2));

export const getCircleHornVertices = (
  output: IHornDesignResponse,
  isElliptical?: Boolean
) => {
  const { hWidth, hHeight, hXval } = output;

  return hXval.map((_, i) => {
    const height = hHeight[i];
    const width = hWidth[i];
    const x = hXval[i];

    if (isElliptical) return new Vector3(-1 * x, -1 * height, -1 * width);
    return new Vector3(x, height, width);
  });
};

export const getVerticesAndFaces = (output: IHornDesignResponse) => {
  const { hCornerRad, hWidth, hHeight, hXval } = output;
  const fanVertices1 = [];
  const fanVertices2 = [];
  const fanVertices3 = [];
  const fanVertices4 = [];
  const edgeVertices1 = [];
  const edgeVertices2 = [];
  const edgeVertices3 = [];
  const edgeVertices4 = [];
  for (let i = 0; i < output.hXval.length; i++) {
    const height = hHeight[i];
    const width = hWidth[i];
    const x = hXval[i];
    const corner = hCornerRad[i];

    if (hCornerRad[i] <= 0) {
      edgeVertices1.push(new Vector3(x, height, -1 * width));
      edgeVertices2.push(new Vector3(x, height, width));
      edgeVertices3.push(new Vector3(x, -1 * height, width));
      edgeVertices4.push(new Vector3(x, -1 * height, -1 * width));
      continue;
    }

    for (let j = 0; j < 10; j++) {
      const angle = ((Math.PI / 2) * j) / 10;
      const ca = Math.cos(angle);
      const sa = Math.sin(angle);
      const corner_y1 = height + corner * (ca - 1);
      const corner_z1 = width + corner * (sa - 1);
      fanVertices1.push(new Vector3(x, corner_y1, -1 * corner_z1));
      fanVertices2.push(new Vector3(x, corner_y1, corner_z1));
      fanVertices3.push(new Vector3(x, -1 * corner_y1, corner_z1));
      fanVertices4.push(new Vector3(x, -1 * corner_y1, -1 * corner_z1));
      if ((hCornerRad[i + 2] === 0 || hCornerRad[i + 1] === 0) && j === 9) {
        edgeVertices1.push(new Vector3(x, corner_y1, -1 * corner_z1));
        edgeVertices2.push(new Vector3(x, corner_y1, corner_z1));
        edgeVertices3.push(new Vector3(x, -1 * corner_y1, corner_z1));
        edgeVertices4.push(new Vector3(x, -1 * corner_y1, -1 * corner_z1));
      }
    }
  }

  return {
    edgeFaces: getEdgeFaces(edgeVertices1.length),
    fanFaces: getFanFaces(fanVertices1.length),
    fanVertices: fanVertices1
      .concat(fanVertices2)
      .concat(fanVertices3)
      .concat(fanVertices4),
    edgeVertices: edgeVertices1
      .concat(edgeVertices2)
      .concat(edgeVertices3)
      .concat(edgeVertices4),
  };
};

const getEdgeFaces = (edgeVertexLength: number) => {
  const edgeFaces = [];
  for (let j = 0; j < 3; j++) {
    const offset = j * edgeVertexLength;
    for (let i = 1; i < edgeVertexLength - 1; i++) {
      edgeFaces.push(
        new Face3(offset + i, offset + i + 1, offset + i + edgeVertexLength)
      );
      edgeFaces.push(
        new Face3(
          offset + i + 1,
          offset + i + edgeVertexLength,
          offset + i + edgeVertexLength + 1
        )
      );
      if (j === 2) {
        edgeFaces.push(new Face3(i, i + 1, offset + i + edgeVertexLength));
        edgeFaces.push(
          new Face3(
            i + 1,
            offset + i + edgeVertexLength,
            offset + i + edgeVertexLength + 1
          )
        );
      }
    }
  }
  return edgeFaces;
};

const getFanFaces = (fanVertexLength: number) => {
  const fanFaces = [];
  for (let j = 0; j < 4; j++) {
    const offset = j * fanVertexLength;
    for (let i = 0; i < 9; i++) {
      fanFaces.push(
        new Face3(offset + i, offset + i + 1, offset + fanVertexLength - 1)
      );
    }
  }

  const x = fanVertexLength;
  // top
  fanFaces.push(new Face3(0, x - 1, x * 2 - 1));
  // bottom
  fanFaces.push(new Face3(2 * x, 3 * x - 1, 4 * x - 1));
  //left
  fanFaces.push(new Face3(x - 1, 9, 3 * x + 9));
  fanFaces.push(new Face3(x - 1, 4 * x - 1, 3 * x + 9));
  // right
  fanFaces.push(new Face3(2 * x - 1, x + 9, 2 * x + 9));
  fanFaces.push(new Face3(2 * x - 1, 3 * x - 1, 2 * x + 9));
  return fanFaces;
};
